import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, ArrowRight, Package } from "lucide-react";
import { Logo } from "../common/Logo";
import { StarMotif } from "../common/StarMotif";
import { useServices, useIndustries, useEngagementModels, useProducts } from "@/lib/content";

const primaryNav = [
    { label: "What We Do", type: "mega", key: "services" },
    { label: "Solutions", type: "mega", key: "industries" },
    { label: "Engagement", type: "dropdown", key: "engagement", items: [] },
    { label: "Products", type: "mega", key: "products" },
    { label: "Success Stories", to: "/success-stories" },
    { label: "Blog", to: "/blog" },
    { label: "Cost Explorer", to: "/cost-explorer" },
];

export function Header() {
    const [openMega, setOpenMega] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");
    const { items: SERVICES } = useServices();
    const { items: INDUSTRIES } = useIndustries();
    const { items: ENGAGEMENT_MODELS } = useEngagementModels();
    const { items: PRODUCTS } = useProducts();
    const engagementItems = ENGAGEMENT_MODELS.map((m) => ({ label: m.name, to: `/engagement-models#${m.slug}` }));
    const nav = primaryNav.map((n) => n.key === "engagement" ? { ...n, items: engagementItems } : n);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setOpenMega(null);
        setSearchOpen(false);
        setMobileProductsOpen(false);
    }, [location.pathname]);

    const submitSearch = (e) => {
        e.preventDefault();
        if (q.trim().length >= 2) {
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
        }
    };

    const isMegaOpen = openMega && ["services", "industries", "products"].includes(openMega);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 w-full transition-all duration-300 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-border shadow-[0_1px_0_hsl(0_0%_0%/0.03)]"
                    : "bg-transparent"
            }`}
            onMouseLeave={() => setOpenMega(null)}
        >
            <div className="container-app flex h-16 lg:h-20 items-center justify-between gap-6">
                <Logo />

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
                    {nav.map((item) => (
                        <div
                            key={item.label}
                            className="relative"
                            onMouseEnter={() => (item.type === "mega" || item.type === "dropdown") ? setOpenMega(item.key) : setOpenMega(null)}
                        >
                            {item.type === "mega" || item.type === "dropdown" ? (
                                <button
                                    type="button"
                                    data-testid={`nav-${item.key}-trigger`}
                                    className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground/85 hover:text-foreground transition-colors rounded-md whitespace-nowrap"
                                    onClick={() => setOpenMega(openMega === item.key ? null : item.key)}
                                    aria-expanded={openMega === item.key}
                                >
                                    {item.label}
                                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${openMega === item.key ? "rotate-180" : ""}`} strokeWidth={1.75} />
                                </button>
                            ) : (
                                <Link
                                    to={item.to}
                                    data-testid={`nav-${item.to.replace(/^\//, "")}-link`}
                                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-foreground/85 hover:text-foreground transition-colors rounded-md whitespace-nowrap"
                                >
                                    {item.label}
                                </Link>
                            )}

                            {/* Small dropdown panel (Engagement) */}
                            {item.type === "dropdown" && openMega === item.key && (
                                <div
                                    className="absolute left-0 top-full mt-1 min-w-[220px] rounded-lg border border-border bg-white shadow-lg overflow-hidden"
                                    data-testid={`nav-${item.key}-dropdown`}
                                >
                                    {item.items.map((sub) => (
                                        <Link
                                            key={sub.to}
                                            to={sub.to}
                                            data-testid={`nav-${item.key}-${sub.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                            className="block px-4 py-3 text-sm font-medium text-foreground/85 hover:bg-secondary/60 hover:text-primary transition-colors"
                                        >
                                            {sub.label}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setSearchOpen((v) => !v)}
                        data-testid="header-search-toggle"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
                        aria-label="Search"
                    >
                        <Search className="h-4 w-4" strokeWidth={1.75} />
                    </button>

                    <Link
                        to="/schedule-meeting"
                        data-testid="header-schedule-meeting-btn"
                        className="hidden sm:inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground btn-lift hover:bg-primary/90 whitespace-nowrap"
                    >
                        Schedule Meeting
                        <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </Link>

                    <button
                        type="button"
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground hover:bg-muted transition-colors"
                        aria-label="Open menu"
                    >
                        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {/* Search bar */}
            {searchOpen && (
                <div className="border-t border-border bg-white/95 backdrop-blur-xl" data-testid="header-search-panel">
                    <div className="container-app py-4">
                        <form onSubmit={submitSearch} className="flex items-center gap-3">
                            <Search className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                            <input
                                data-testid="header-search-input"
                                autoFocus
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search services, industries, products, blogs..."
                                className="flex-1 bg-transparent outline-none text-base placeholder:text-muted-foreground"
                            />
                            <button
                                type="submit"
                                data-testid="header-search-submit"
                                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                            >
                                Search
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Mega menu panel */}
            {isMegaOpen && (
                <div className="hidden lg:block border-t border-border bg-white/95 backdrop-blur-xl shadow-lg" data-testid={`mega-menu-${openMega}`}>
                    <div className="container-app py-8">

                        {/* Services */}
                        {openMega === "services" && (
                            <div className="grid grid-cols-4 gap-6">
                                <div className="col-span-1">
                                    <span className="overline">What We Do</span>
                                    <h3 className="mt-2 font-heading text-2xl font-bold leading-tight">Engineering pods across the full modern stack.</h3>
                                    <p className="mt-3 text-sm text-muted-foreground">Seven practice areas, one accountability line — senior engineers who ship.</p>
                                    <Link to="/what-we-do" data-testid="mega-services-overview" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                                        See all services <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    </Link>
                                </div>
                                <div className="col-span-3 grid grid-cols-2 gap-x-6 gap-y-1">
                                    {SERVICES.map((s) => (
                                        <Link key={s.slug} to={`/what-we-do/${s.slug}`} data-testid={`mega-service-${s.slug}`} className="group flex items-start gap-3 rounded-lg p-3 -mx-3 hover:bg-secondary/60 transition-colors">
                                            <span className="mt-1 h-8 w-8 flex-shrink-0 rounded-md bg-secondary text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors inline-flex items-center justify-center">
                                                <StarMotif className="h-4 w-4" label="RKS" labelColor="currentColor" />
                                            </span>
                                            <span>
                                                <span className="block font-heading font-semibold text-sm text-foreground">{s.title}</span>
                                                <span className="block text-xs text-muted-foreground mt-0.5 leading-relaxed">{s.tagline}</span>
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Industries */}
                        {openMega === "industries" && (
                            <div className="grid grid-cols-4 gap-6">
                                <div className="col-span-1">
                                    <span className="overline">Solutions</span>
                                    <h3 className="mt-2 font-heading text-2xl font-bold leading-tight">Industry-shaped outcomes.</h3>
                                    <p className="mt-3 text-sm text-muted-foreground">Deep domain patterns and reference architectures for eight core industries.</p>
                                    <Link to="/solutions" data-testid="mega-industries-overview" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                                        See all industries <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    </Link>
                                </div>
                                <div className="col-span-3 grid grid-cols-2 gap-x-6 gap-y-1">
                                    {INDUSTRIES.map((i) => (
                                        <Link key={i.slug} to={`/solutions/${i.slug}`} data-testid={`mega-industry-${i.slug}`} className="group flex items-start gap-3 rounded-lg p-3 -mx-3 hover:bg-secondary/60 transition-colors">
                                            <span className="mt-1 h-8 w-8 flex-shrink-0 rounded-md bg-secondary text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors inline-flex items-center justify-center text-xs font-bold">
                                                {i.name.substring(0, 2).toUpperCase()}
                                            </span>
                                            <span>
                                                <span className="block font-heading font-semibold text-sm text-foreground">{i.name}</span>
                                                <span className="block text-xs text-muted-foreground mt-0.5 leading-relaxed">{i.headline}</span>
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Products */}
                        {openMega === "products" && (
                            <div className="grid grid-cols-4 gap-6">
                                <div className="col-span-1">
                                    <span className="overline">Products</span>
                                    <h3 className="mt-2 font-heading text-2xl font-bold leading-tight">Production-grade products.</h3>
                                    <p className="mt-3 text-sm text-muted-foreground">Real solutions for real business problems — with the architecture, features, and outcomes.</p>
                                    <Link to="/products" data-testid="mega-products-overview" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                                        View all products <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    </Link>
                                </div>
                                <div className="col-span-3 grid grid-cols-2 gap-x-6 gap-y-1">
                                    {PRODUCTS.slice(0, 6).map((p) => (
                                        <Link
                                            key={p.id}
                                            to={`/products/${p.id}`}
                                            data-testid={`mega-product-${p.id}`}
                                            className="group flex items-start gap-3 rounded-lg p-3 -mx-3 hover:bg-secondary/60 transition-colors"
                                        >
                                            <span className="mt-1 h-8 w-8 flex-shrink-0 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors inline-flex items-center justify-center">
                                                <Package className="h-4 w-4" />
                                            </span>
                                            <span>
                                                <span className="block font-heading font-semibold text-sm text-foreground leading-snug">{p.title}</span>
                                                <span className="block text-xs text-muted-foreground mt-0.5 leading-relaxed line-clamp-2">{p.description}</span>
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            )}

            {/* Mobile menu */}
            {mobileOpen && (
                <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-menu-panel">
                    <nav className="container-app py-4 flex flex-col gap-1">
                        <Link to="/what-we-do" data-testid="mobile-nav-services" className="px-3 py-3 rounded-md font-medium hover:bg-muted">What We Do</Link>
                        <Link to="/solutions" data-testid="mobile-nav-solutions" className="px-3 py-3 rounded-md font-medium hover:bg-muted">Solutions</Link>
                        <Link to="/engagement-models" data-testid="mobile-nav-engagement" className="px-3 py-3 rounded-md font-medium hover:bg-muted">Engagement Models</Link>

                        {/* Mobile Products accordion */}
                        <div>
                            <button
                                type="button"
                                data-testid="mobile-nav-products-toggle"
                                className="w-full flex items-center justify-between px-3 py-3 rounded-md font-medium hover:bg-muted text-left"
                                onClick={() => setMobileProductsOpen((v) => !v)}
                            >
                                <span>Products</span>
                                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileProductsOpen ? "rotate-180" : ""}`} strokeWidth={1.75} />
                            </button>
                            {mobileProductsOpen && (
                                <div className="ml-3 mt-1 mb-2 flex flex-col gap-0.5 border-l-2 border-primary/20 pl-3">
                                    {PRODUCTS.map((p) => (
                                        <Link
                                            key={p.id}
                                            to={`/products/${p.id}`}
                                            data-testid={`mobile-product-${p.id}`}
                                            className="block px-2 py-2.5 rounded-md hover:bg-muted"
                                        >
                                            <span className="block text-sm font-semibold text-foreground leading-snug">{p.title}</span>
                                            <span className="block text-xs text-muted-foreground mt-0.5 line-clamp-1">{p.description}</span>
                                        </Link>
                                    ))}
                                    <Link to="/products" className="mt-1 flex items-center gap-1 px-2 py-2 text-xs font-semibold text-primary hover:underline">
                                        View all products <ArrowRight className="h-3 w-3" />
                                    </Link>
                                </div>
                            )}
                        </div>

                        <Link to="/success-stories" data-testid="mobile-nav-stories" className="px-3 py-3 rounded-md font-medium hover:bg-muted">Success Stories</Link>
                        <Link to="/blog" data-testid="mobile-nav-blog" className="px-3 py-3 rounded-md font-medium hover:bg-muted">Blog</Link>
                        <Link to="/cost-explorer" data-testid="mobile-nav-cost" className="px-3 py-3 rounded-md font-medium hover:bg-muted">Cost Explorer</Link>
                        <Link to="/contact" data-testid="mobile-nav-contact" className="px-3 py-3 rounded-md font-medium hover:bg-muted">Contact</Link>
                        <Link
                            to="/schedule-meeting"
                            data-testid="mobile-schedule-meeting"
                            className="mt-2 inline-flex items-center justify-center rounded-md bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground"
                        >
                            Schedule Meeting
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
}

export default Header;

